import React from 'react';
import styles from './ItemCard.module.css';
import TimeSlot from './TimeSlot';
import { ItineraryItem } from 'models/itinerary';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useSelector } from 'react-redux';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { Handle } from './Handle';
import { TravelTime } from './TravelTime';
import { DigitalMapPin } from 'models/digitalMap';
import { findActivePinSchedule, formattedTimeOfDay } from 'components/Map/util';
import moment from 'moment';
import { TimePickerModal } from './TimePickerModal';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

const getPinScheduleItemsForDate = (
  digitalMapPin: DigitalMapPin | undefined,
  date: string,
  language: string
) => {
  if (!digitalMapPin) {
    return [];
  }

  if (digitalMapPin.time_data_type === 'TIME_RANGE' && digitalMapPin.open_hour_schedules?.length) {
    const activeSchedule = findActivePinSchedule(
      digitalMapPin.open_hour_schedules,
      moment(date, 'YYYY-MM-DD')
    );

    if (!activeSchedule) {
      return [];
    }

    return [
      `${formattedTimeOfDay(activeSchedule.start_time_local, language)} - ${formattedTimeOfDay(
        activeSchedule.end_time_local,
        language
      )}`,
    ];
  } else if (
    digitalMapPin.time_data_type === 'SHOWTIMES' &&
    digitalMapPin.showtime_schedules?.length
  ) {
    const activeSchedule = findActivePinSchedule(
      digitalMapPin.showtime_schedules,
      moment(date, 'YYYY-MM-DD')
    );

    if (!activeSchedule) {
      return [];
    }

    return activeSchedule.showtimes?.map((showTime) => formattedTimeOfDay(showTime, language));
  }

  return [];
};

interface ItemCardProps {
  item: ItineraryItem;
  onDelete: () => void;
  onTimeChange: (newTime: string) => void;
  date: string;
  defaultTime: string;
}

export const ItemCard: React.FC<ItemCardProps> = ({
  item,
  onDelete,
  onTimeChange,
  date,
  defaultTime,
}) => {
  const { t, i18n } = useTranslation();
  const [isTimeModalOpen, setIsTimeModalOpen] = React.useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.pin_key,
  });

  const digitalMap = useSelector(selectDigitalMap);
  const pin = digitalMap?.pins?.find((pin) => pin.key === item.pin_key);
  const thumbnailUrl = pin?.media_items?.find((media) => media.type === 'IMAGE')?.url;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const scheduleItems = getPinScheduleItemsForDate(pin, date, i18n.language);

  return (
    <>
      <div ref={setNodeRef} style={style} className={styles.card}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={pin?.title} className={styles.cardImage} />
        ) : (
          <div className={styles.cardImage} />
        )}
        <div className={styles.cardContent}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>
              <div className={styles.titleRow}>
                <div className={styles.showTitle}>{pin?.title}</div>
                <TravelTime estimatedTime={item.travel_time} />
              </div>
              <div className={styles.timeSelectorRow}>
                <div
                  className={clsx(styles.timeSelector, !item.time && styles.timeSelectorEmpty)}
                  onClick={() => {
                    if (!item.time) {
                      onTimeChange(defaultTime);
                    }
                    setIsTimeModalOpen(true);
                  }}
                >
                  <div className={styles.selectTimeText}>
                    {item.time ? formattedTimeOfDay(item.time, i18n.language) : t('Select Time')}
                  </div>
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets/TEMP/c03286c49e0fbd6581e6ecd69f42a1b85afcca69c03041442ce3fabddbd5d523"
                    alt=""
                    className={styles.dropdownIcon}
                  />
                </div>
                <img
                  onClick={onDelete}
                  src="/static/images/map/trash_outline.svg"
                  className={styles.locationIcon}
                  alt=""
                />
              </div>
            </div>
          </div>
          {scheduleItems.length > 0 && (
            <div className={styles.timeSlots}>
              <div className={styles.timeSlotContainer}>
                {scheduleItems.map((time, index) => (
                  <TimeSlot key={index} time={time} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.dragHandle} {...attributes} {...listeners}>
          <Handle />
        </div>
      </div>

      {isTimeModalOpen && (
        <TimePickerModal
          open={true}
          onClose={() => setIsTimeModalOpen(false)}
          value={item.time}
          onChange={onTimeChange}
        />
      )}
    </>
  );
};
